import { useRef, useEffect } from 'react';
import { useListState } from '@react-stately/list';
import { Item } from '@react-stately/collections';
import { useListBox } from '@react-aria/listbox';

import type { ReactElement, ReactNode, RefObject } from 'react';
import type { ListProps } from '@react-stately/list';
import type { AriaListBoxOptions } from '@react-aria/listbox';

import { usePreventWindowScroll, Portal } from '@simplywallst/ui-core';
import { TextField, Button, getCSSVar } from '@simplywallst/ui-core/next';

import { focusAndOpenKeyboard } from '@/utilities/focusAndOpenKeyboard';

import { Option, type ListBoxOptionItemProps } from './Option';
import { ResetButton } from './ResetButton';

interface Props<T>
  extends Pick<
      ListProps<T>,
      'children' | 'onSelectionChange' | 'selectedKeys' | 'items'
    >,
    Pick<AriaListBoxOptions<T>, 'label' | 'onFocus'> {
  inputValue?: string;
  placeholder?: string;
  onCancel: () => void;
  onInputChange?: (value?: string) => void;
  emptyStateRender?: () => ReactNode;
  status: 'pending' | 'success' | 'error';
  containerRef: RefObject<HTMLDivElement>;
}

const SearchDrawer = <T extends object>({
  label,
  selectedKeys,
  placeholder,
  children,
  items,
  inputValue,
  onCancel,
  emptyStateRender,
  onInputChange,
  onSelectionChange,
  onFocus,
  status,
  containerRef,
}: Props<T>) => {
  const listBoxRef = useRef(null);

  usePreventWindowScroll();

  const inputRef = useRef<HTMLInputElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const resetButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (containerRef.current !== null && drawerRef.current !== null) {
      // Get the current cascade of theme display for the parent element from CSS, so we can apply
      // it to the drawer element directly.
      // This is due to the portal causing a break in the 'normal' DOM cascade flow of CSS and
      // allows sections of the app to be light/dark mode.
      const theme = getCSSVar('--s-theme', containerRef.current);
      drawerRef.current.dataset['theme'] = theme;
    }
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      focusAndOpenKeyboard(inputRef.current, 300);
    }
  }, []);

  const state = useListState({
    children,
    items,
    selectedKeys,
    selectionMode: 'single',
    selectionBehavior: 'replace',
    onSelectionChange,
  });

  const { listBoxProps } = useListBox(
    {
      label,
    },
    state,
    listBoxRef
  );

  const { collection } = state;

  const isIdleState =
    (inputValue?.length === 0 || status === 'pending') && collection.size === 0;

  return (
    <Portal>
      <div
        data-cy-id="search-drawer"
        className="click-outside-ignore fixed inset-0 z-[9999] bg-surface-1"
        ref={drawerRef}
      >
        <div className="pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]">
          <div className="grid h-[calc(100vh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] w-full grid-rows-[min-content_1fr] pb-4">
            <div className="grid grid-cols-[auto_60px] gap-2 p-4">
              <TextField
                ref={inputRef}
                value={inputValue}
                placeholder={placeholder}
                onChange={onInputChange}
                onFocus={onFocus}
                isLoading={
                  Number(inputValue?.length) > 0 && status === 'pending'
                }
                postfix={
                  <ResetButton
                    ref={resetButtonRef}
                    onClick={() => onInputChange?.('')}
                  />
                }
                aria-label={typeof label === 'string' ? label : undefined}
              />
              <Button variant="subtle" onClick={onCancel}>
                Cancel
              </Button>
            </div>
            <div className="block overflow-y-auto overflow-x-scroll">
              {!isIdleState ? (
                <ul
                  {...listBoxProps}
                  ref={listBoxRef}
                  className="m-0 list-none p-0"
                >
                  {collection.size === 0 && emptyStateRender?.()}
                  {[...collection].map((item) => (
                    <Option key={item.key} item={item} state={state} />
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

SearchDrawer.Option = Item as (
  props: { key: string; textValue: string } & ListBoxOptionItemProps
) => ReactElement;

export { SearchDrawer };
